import {
  Description as DescriptionIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { TransactionPurpose } from 'lib/api/express/transaction/types';
import ProviderSelect from 'lib/common/Transaction/ProviderSelect';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useState } from 'react';
import { useMemo } from 'react';

interface PaymentDocumentProps {
  warranty?: Warranty;
  pruffConnect?: boolean;
}

export default function PaymentDocument({
  warranty,
  pruffConnect = false,
}: PaymentDocumentProps) {
  const [open, setOpen] = useState(false);
  const document = warranty?.documents?.[0];

  const hasRates =
    !!warranty?.operationCostFixed ||
    !!warranty?.operationCostVariable ||
    !!warranty?.discountRateFixed ||
    !!warranty?.discountRateVariable;

  const efectiveFixedRate = useMemo(() => {
    return (
      (warranty?.operationCostFixed ?? 0) *
      (1 - (warranty?.discountRateFixed ?? 0) / 100)
    );
  }, [warranty]);

  const efectiveVariableRate = useMemo(() => {
    return (
      (warranty?.operationCostVariable ?? 0) *
      (1 - (warranty?.discountRateVariable ?? 0) / 100)
    );
  }, [warranty]);

  const [showRatesTooltip, setShowRatesTooltip] = useState(false);

  return (
    <>
      <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <DescriptionIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Documento de Pago
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={'Costo de emisión'}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              <Chip
                label={document?.paidAt ? 'Pagado' : 'Pendiente'}
                size="small"
                color={document?.paidAt ? 'success' : 'warning'}
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Stack>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              {hasRates && (
                <Box
                  sx={{ position: 'relative', ml: 0.25 }}
                  onMouseEnter={() => setShowRatesTooltip(true)}
                  onMouseLeave={() => setShowRatesTooltip(false)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 0.3,
                      cursor: 'pointer',
                      color: 'text.secondary',
                    }}
                  >
                    <InfoIcon />
                  </Box>

                  {showRatesTooltip && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 0.75,
                        zIndex: 10,
                      }}
                    >
                      <Stack
                        sx={{
                          p: 1.25,
                          minWidth: 230,
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            mb: 0.75,
                            color: 'text.primary',
                            textAlign: 'left',
                          }}
                        >
                          Tasas aplicadas
                        </Typography>

                        {warranty?.operationCostFixed != null && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.25 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tasa fija comisión
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {warranty.operationCostFixed} UF
                            </Typography>
                          </Stack>
                        )}

                        {warranty?.discountRateFixed != null && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.25 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tasa fija descuento
                            </Typography>
                            <Typography variant="caption" fontWeight={900}>
                              {warranty.discountRateFixed}%
                            </Typography>
                          </Stack>
                        )}
                        {efectiveFixedRate != null && (
                          <Box
                            sx={{
                              mb: 0.75,
                              mt: 0.5,
                              p: 0.75,
                              borderRadius: 1,
                              border: '1px solid rgba(25, 118, 210, 0.4)',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: 'primary.main' }}
                              >
                                Tasa fija efectiva
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="primary.main"
                              >
                                {efectiveFixedRate.toFixed(2)} UF
                              </Typography>
                            </Stack>
                          </Box>
                        )}

                        {warranty?.operationCostVariable != null && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.25 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tasa variable comisión
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {warranty.operationCostVariable}%
                            </Typography>
                          </Stack>
                        )}

                        {warranty?.discountRateVariable != null && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tasa variable descuento
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {warranty.discountRateVariable}%
                            </Typography>
                          </Stack>
                        )}

                        {efectiveVariableRate != null && (
                          <Box
                            sx={{
                              mt: 0.5,
                              p: 0.75,
                              borderRadius: 1,
                              border: '1px solid rgba(25, 118, 210, 0.4)',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: 'primary.main' }}
                              >
                                Tasa variable efectiva
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="primary.main"
                              >
                                {efectiveVariableRate.toFixed(2)}%
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
              <Typography variant="h4" color="primary">
                {formatMoney(document?.amount ?? 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <AlertBanner
          severity="info"
          title="Información Importante"
          message={
            document?.paidAt
              ? 'El pago ha sido conciliado con exito. El certificado de garantía será emitido automáticamente.'
              : 'El certificado de garantía no será emitido hasta que se realice y verifique el pago correspondiente.'
          }
        />

        {warranty?.status === 'APPROVED' && pruffConnect && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
              width: '100%',
            }}
          >
            <CustomButton color="primary" onClick={() => setOpen(true)}>
              Pagar
            </CustomButton>
          </Box>
        )}
      </Paper>
      <ProviderSelect
        open={open}
        onClose={() => setOpen(false)}
        amount={document?.amount ?? 0}
        purpose={TransactionPurpose.WARRANTY_PREMIUM}
        returnUrl={window.location.origin + '/payment-commit'}
        requestId={warranty?.id}
      />
    </>
  );
}
